import { profileFilters as filters } from "../../helpers/constants";
import ReactionItem from "../partials/Profile/ReactionItem";
import AnsweredItem from "../partials/Profile/AnsweredItem";
import BookmarkItem from "../partials/Profile/BookmarkItem";
import React, { useEffect, useState } from "react";
import AllItem from "../partials/Profile/AllItem";
import PostItem from "../Home/PostItem";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchAllByMe,
  fetchMyAnsweredPosts,
  fetchMyReactedPosts,
  fetchMyReadLaterPosts,
  fetchRepliesByMe,
  fetchTopicsCreatedByMe,
} from "../../helpers/querycalls";
import PostShimmer from "../partials/postShimmer";
import { Helmet } from "react-helmet";

const HALF_OF_A_MINUTE_IN_MILLISECONDS = 30 * 1000;
export const Profile = () => {
  // =======================
  // useStates
  // =======================
  const [selectedFilter, setSelectedFilter] = useState("all");

  // =======================
  // Admin Authentication
  // =======================
  const userObj = localStorage.getItem("salla_discourse_user");
  const user = JSON.parse(userObj);
  let username;
  let user_id;
  let isLoggedIn = false;

  if (user) {
    isLoggedIn = true;
    username = user.username;
    // eslint-disable-next-line no-unused-vars
    user_id = user.id;
  }

  // =======================
  // useLocation
  // =======================
  const location = useLocation();
  const profileUserName = location.state?.username;
  const profileUserId = location.state?.id;

  // const [userId, setUserId] = useState(location.state?.id);
  useEffect(() => {
    if (location.state?.id) {
      setSelectedFilter("all");
      // setUserId(location.state.id);
    }
  }, [location.state?.id]);
  // =======================
  // User social handles extraction
  // =======================

  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);

  const [image, setImage] = useState("");

  // =======================
  // useEffects
  // =======================

  const fetchFunctions = {
    all: () => fetchAllByMe(profileUserName),
    topics: () => fetchTopicsCreatedByMe(profileUserName),
    posts: () => fetchRepliesByMe(profileUserName),
    likes: () => fetchMyReactedPosts(profileUserName),
    answers: () => fetchMyAnsweredPosts(profileUserName),
    read_later: () => fetchMyReadLaterPosts(profileUserName),
  };

  const {
    data: posts,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["posts", selectedFilter, location.state?.id],
    queryFn: fetchFunctions[selectedFilter],
    staleTime: HALF_OF_A_MINUTE_IN_MILLISECONDS, // consider the data fresh for a half of a minute
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!selectedFilter, // Ensures the query only runs if dependencies are truthy
  });

  useEffect(() => {
    const fetchProfileActivityCount = async () => {
      await getProfileInformation(profileUserName, profileUserId);
    };

    fetchProfileActivityCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUserName, profileUserId, location.state?.id]);

  // =======================
  // Render Posts By Filter
  // =======================
  function renderTopicsByFilter() {
    if (isLoading) {
      return (
        <div className="flex flex-col justify-center gap-y-2">
          {[...Array(6)].map((_, index) => (
            <PostShimmer key={index}></PostShimmer>
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <p className="text-center">
          يبدو أن هناك مشكلة أثناء تحميل البيانات. يرجى المحاولة مرة أخرى بعد
          مرور بعض الوقت
        </p>
      );
    }
    switch (selectedFilter) {
      case "topics":
        return posts?.map((post) => (
          <React.Fragment key={post.id}>
            <PostItem
              post={post}
              isLoggedIn={isLoggedIn}
              loggedInUserId={location.state?.id}
            />
          </React.Fragment>
        ));
      case "all":
      case "posts":
        return posts?.map((post) => (
          <React.Fragment key={post.id}>
            <AllItem post={post} />
          </React.Fragment>
        ));
      case "answers":
        return posts?.map((post) => (
          <React.Fragment key={post.id}>
            <AnsweredItem post={post} />
          </React.Fragment>
        ));
      case "likes": //like
        return posts?.map((post) => (
          <React.Fragment key={post.id}>
            <ReactionItem post={post} />
          </React.Fragment>
        ));
      case "read_later":
        return posts?.map((post) => (
          <React.Fragment key={post.id}>
            <BookmarkItem post={post} />
          </React.Fragment>
        ));
      default:
        return <p>لا توجد بيانات لعرضها</p>; // Fallback message
    }
  }

  // =======================
  // APIs
  // =======================
  async function getProfileInformation(profileUserName, profileUserId) {
    try {
      setLoading(true);
      if (!profileUserName && user) {
        username = user.username;
      } else {
        username = profileUserName;
      }
      if (!profileUserId && user) {
        user_id = user.id;
      } else {
        user_id = profileUserId;
      }
      const data = {
        username: username,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/notification_counts/activity_count.json`,
        data
      );
      const {
        all,
        topics,
        posts,
        likes,
        answers,
        bookmarks,
        avatar_template,
        bio,
        name,
        username: userUsername,
        website,
        custom_fields,
      } = response.data;

      setUserData({
        all,
        topics,
        posts,
        likes,
        answers,
        bookmarks,
        bio,
        name,
        username: userUsername,
        website,
        custom_fields,
      });

      setImage(avatar_template?.replace("{size}", "200"));
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  }

  return (
    <div
      className="flex flex-col items-center xl:!w-full min-1100:w-[75%] lg:w-[73%] min-920:w-[70%] min-820:w-[67%] md:w-[65%] w-full mt-[18px] gap-[20px]"
      dir="rtl"
    >
      <Helmet>
        <title>الملف الشخصي</title>
        <meta name="description" content="الملف الشخصي" />
      </Helmet>
      {loading && user && (
        <div className="flex flex-col items-center gap-4">
          <div className="shimmer w-[124px] h-[124px] rounded-full"></div>
          <div className="shimmer w-[300px] h-4 rounded-full"></div>
          <div className="shimmer w-[200px] h-4 rounded-full"></div>
          <div className="shimmer w-[75px] h-4 rounded-full"></div>
          <div className="shimmer w-[120px] h-10 rounded-full"></div>
          <div className="flex gap-3">
            <div className="shimmer w-[64px] h-11 rounded-full"></div>
            <div className="shimmer w-[64px] h-11 rounded-full"></div>
            <div className="shimmer w-[64px] h-11 rounded-full"></div>
            <div className="shimmer w-[64px] h-11 rounded-full"></div>
          </div>
        </div>
      )}
      {!loading && (
        <div className="flex flex-col items-center">
          {/* name, image container */}
          <div className="flex flex-col items-center">
            <img
              src={image}
              alt="avatar"
              className="w-[124px] h-[124px] rounded-full"
              onError={(e) => {
                e.target.src = `${process.env.REACT_APP_API_URL}` + image;
              }}
            />
            <p className="font-bold text-[25px] mt-[20px]">{userData?.name} </p>
            <p className="text-[16px]">{userData?.username}</p>
          </div>
          {/* social container */}
          <div className="flex flex-col items-center text-center my-3 max-w-[1005px] px-4">
            <p className="break-words">{userData?.bio}</p>
          </div>
          {/* store link container */}
          {userData.custom_fields?.[2] && (
            <div className="flex flex-col items-center">
              <div className="flex justify-center gap-[8px] mt-[20px] border hover:bg-[#76e8cd] border-[#76e8cd] rounded-[100px] py-3 px-5">
                <a
                  href={
                    userData.custom_fields?.[2].startsWith("http")
                      ? userData.custom_fields?.[2]
                      : `https://${userData.custom_fields?.[2]}`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="flex gap-2 items-center rounded-lg">
                    {userData.custom_fields && (
                      <img src="/images/salla-logo-black.svg" alt="logo" />
                    )}
                    {userData.custom_fields && userData.custom_fields?.[1] && (
                      <p className="font-[16px] ">
                        {userData.custom_fields?.[1]}
                      </p>
                    )}
                  </div>
                </a>
              </div>
              <div className="flex justify-center gap-2.5 mt-[20px]">
                {userData.website && (
                  <a
                    href={
                      userData.website.startsWith("http")
                        ? userData.website
                        : `https://${userData.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center hover:bg-gray-200 py-3 px-5 border rounded-full">
                      <img
                        src="/images/copy-clip.svg"
                        className="cursor-pointer"
                        alt="Website"
                      />
                    </div>
                  </a>
                )}
                {userData.custom_fields && userData.custom_fields?.[5] && (
                  <a
                    href={
                      userData.custom_fields?.[5].startsWith("http")
                        ? userData.custom_fields?.[5]
                        : `https://${userData.custom_fields?.[5]}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center hover:bg-gray-200 py-3 px-5 border rounded-full">
                      <img
                        src="/images/tiktok.svg"
                        className="cursor-pointer"
                        alt="TikTok"
                      />
                    </div>
                  </a>
                )}
                {userData.custom_fields && userData.custom_fields?.[4] && (
                  <a
                    href={
                      userData.custom_fields?.[4].startsWith("http")
                        ? userData.custom_fields?.[4]
                        : `https://${userData.custom_fields?.[4]}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center hover:bg-gray-200 py-3 px-5 border rounded-full">
                      <img
                        src="/images/insta.svg"
                        className="cursor-pointer"
                        alt="Instagram"
                      />
                    </div>
                  </a>
                )}
                {userData.custom_fields && userData.custom_fields?.[3] && (
                  <a
                    href={
                      userData.custom_fields?.[3].startsWith("http")
                        ? userData.custom_fields?.[3]
                        : `https://${userData.custom_fields?.[3]}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center hover:bg-gray-200 py-3 px-5 border rounded-full">
                      <img
                        src="/images/twitter.svg"
                        className="cursor-pointer"
                        alt="Twitter"
                      />
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* posts container */}
      <div className="flex flex-col sm:px-6 px-4 max-w-[1005px] w-full xl:mx-auto ">
        <div className="flex justify-center w-full sm:pt-10 pb-3 pt-3 bg-[#f8f8f8] ">
          {/* filters */}
          <div className="flex justify-between gap-3  sm:gap-[26px] mb-10 overflow-x-auto">
            {filters.map((filter, index) => {
              return (
                (filter.good_name !== "read_later" ||
                  user?.id === location.state?.id) && (
                  <div
                    key={index}
                    className={`${
                      selectedFilter === filter.good_name
                        ? "border-b-[1px] border-b-black"
                        : ""
                    } hover:border-b-[1px] hover:border-b-black flex text-[14px] sm:flex-row flex-col-reverse items-center justify-center py-[12px] px-[18px] sm:px-[30px] gap-3  cursor-pointer`}
                    onClick={() => setSelectedFilter(filter.good_name)}
                  >
                    <p className="whitespace-nowrap text-[#333] text-sm">
                      {filter.filter_name}
                    </p>
                    {!loading && (
                      <p className="font-bold">
                        {filter.good_name === "all" && userData.all}
                        {filter.good_name === "topics" && userData.topics}
                        {filter.good_name === "posts" && userData.posts}
                        {filter.good_name === "likes" && userData.likes}
                        {filter.good_name === "answers" && userData.answers}
                        {filter.good_name === "read_later" &&
                          userData.bookmarks}
                      </p>
                    )}
                    {loading && (
                      <img
                        loading="eager"
                        src="/images/salla_loader_big.svg"
                        alt="Loading..."
                        className="w-[12px] h-[15px] loader"
                      />
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>
        {renderTopicsByFilter()}
      </div>
    </div>
  );
};
